import React, { useCallback, useMemo, useState } from 'react';
import { Requirement } from '../../types/data';
import { DrawerComponent } from 'src/components/DialogComponent';
import { FlexCol } from 'src/utils/flexLayouts';
import AddActionGop from '../formAddActionGop/AddActionGop';
import { PlanActionsContext } from '../../types/types';
import { useAppDispatch, useAppSelector } from 'src/hooks/redux';
import { fetchActionByContextRequest } from '../../store/link-actions/actions';
import { fetchActionsOnlyRequest } from '../../store/data-main/data-main';
import { deleteData, setLastAddedId } from '../../actions/actions';
import { selectLastDocId, selectLastAddedId } from '../../selectors/selectors';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { selectItems } from 'src/modules/spItems/selectors';
import { myTaskActionsListName } from 'src/api/config/myTasksActions.config';

type AddButtonProps = Pick<Requirement, 'actions'> & {
  onClick?: () => void;
  context: PlanActionsContext;
};

function AddAction({ actions, onClick, context }: AddButtonProps) {
  const [open, setOpen] = useState(false);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const foundActions = useAppSelector((state) => state.gop.gop.actionByContext?.data || []);
  const lastAddedIdforDelete = useAppSelector((state) => selectLastDocId(state, 'gop-actions'));
  const lastAddedId = useAppSelector((state) => selectLastAddedId(state, 'gop-actions'));

  const firstActionId = useMemo(() => {
    if (foundActions.length > 0 && foundActions[0]?.linkedGopAction?.id) {
      return foundActions[0].linkedGopAction.id;
    }
    return null;
  }, [foundActions]);

  const gopFormId = firstActionId || lastAddedId;

  const innerActions = useAppSelector((state) => selectItems(state, myTaskActionsListName));
  const hasInnerActions = useMemo(() => {
    if (!gopFormId) return false;
    const tag = `#GOPСтандарты_${gopFormId}`;
    return innerActions.some((action: any) => action.ImportantHashTag?.trim() === tag.trim());
  }, [innerActions, gopFormId]);

  const setOpenDrawer = (value: boolean) => {
    if (open !== value) {
      setOpen(value);

      if (value) {
        if (context) {
          dispatch(fetchActionByContextRequest('gop-actions', context));
        }
      } else {
        setSearchParams({});
      }
    }
  };

  React.useEffect(() => {
    if (open && firstActionId) {
      const currentId = searchParams.get('actionCardGOP');
      if (currentId !== String(firstActionId)) {
        navigate(`?actionCardGOP=${firstActionId}`, { replace: true });
      }
    }
  }, [open, firstActionId, searchParams, navigate]);

  React.useEffect(() => {
    const urlGopId = searchParams.get('actionCardGOP');

    if (urlGopId && !open) {
      const urlId = Number(urlGopId);

      if (actions && actions.length > 0 && Number(actions[0]) === urlId) {
        setOpen(true);
        if (context) {
          dispatch(fetchActionByContextRequest('gop-actions', context));
        }
      }
    }
  }, [searchParams, actions, open, context, dispatch]);

const closeDialog = useCallback(() => {
  setOpen(false);
  setSearchParams({});

  if (context?.currentDirection && context?.selectedFactory && context?.currentQuarter && context?.currentYear) {
    dispatch(fetchActionsOnlyRequest({
      factory: context.selectedFactory.id,
      quarter: context.currentQuarter,
      year: context.currentYear,
      direction: context.currentDirection.id,
    }));
  }
}, [context, dispatch, actions, setSearchParams]);

const handleDrawerClose = useCallback(async () => {
  dispatch(setLastAddedId('gop-actions', null));

  if (lastAddedIdforDelete && !actions && !hasInnerActions) {
    await dispatch(deleteData('gop-actions', lastAddedIdforDelete));
    setTimeout(() => {
      closeDialog();
    }, 400);
  } else {
    closeDialog();
  }
}, [lastAddedIdforDelete, dispatch, closeDialog, actions, hasInnerActions]);

  if (actions) {
    return (
      <>
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='16'
          height='16'
          viewBox='0 0 16 16'
          fill='none'
          className='group cursor-pointer'
          onClick={() => {
            if (onClick) onClick();
            setOpenDrawer(true);
          }}
        >
          <g clipPath='url(#clip0_2001_1903)'>
            <path
              d='M8.0002 0.450195C3.8252 0.450195 0.450195 3.8252 0.450195 8.0002C0.450195 12.1752 3.8252 15.5752 8.0002 15.5752C12.1752 15.5752 15.5752 12.1752 15.5752 8.0002C15.5752 3.8252 12.1752 0.450195 8.0002 0.450195ZM8.0002 14.4502C4.4502 14.4502 1.5752 11.5502 1.5752 8.0002C1.5752 4.4502 4.4502 1.5752 8.0002 1.5752C11.5502 1.5752 14.4502 4.47519 14.4502 8.0252C14.4502 11.5502 11.5502 14.4502 8.0002 14.4502Z'
              fill='#2D68F8'
              className='group-hover:hidden'
            />
            <path
              d='M10.1497 5.67533L7.17475 8.57533L5.82475 7.25033C5.59975 7.02533 5.24975 7.05033 5.02475 7.25033C4.79975 7.47533 4.82475 7.82533 5.02475 8.05033L6.62475 9.60033C6.77475 9.75033 6.97475 9.82533 7.17475 9.82533C7.37475 9.82533 7.57475 9.75033 7.72475 9.60033L10.9497 6.50033C11.1747 6.27533 11.1747 5.92533 10.9497 5.70033C10.7247 5.47533 10.3747 5.47533 10.1497 5.67533Z'
              fill='#2D68F8'
              className='group-hover:hidden'
            />

            <path
              xmlns='http://www.w3.org/2000/svg'
              fillRule='evenodd'
              clipRule='evenodd'
              d='M0.450195 8.0002C0.450195 3.8252 3.8252 0.450195 8.0002 0.450195C12.1752 0.450195 15.5752 3.8252 15.5752 8.0002C15.5752 12.1752 12.1752 15.5752 8.0002 15.5752C3.8252 15.5752 0.450195 12.1752 0.450195 8.0002ZM7.17524 8.57517L10.1502 5.67517C10.3752 5.47517 10.7252 5.47517 10.9502 5.70017C11.1752 5.92517 11.1752 6.27517 10.9502 6.50017L7.72523 9.60017C7.57524 9.75017 7.37524 9.82517 7.17524 9.82517C6.97524 9.82517 6.77524 9.75017 6.62524 9.60017L5.02524 8.05017C4.82524 7.82517 4.80024 7.47517 5.02524 7.25017C5.25024 7.05017 5.60023 7.02517 5.82524 7.25017L7.17524 8.57517Z'
              fill='#2D68F8'
              className='hidden group-hover:block'
            />
          </g>
          <defs>
            <clipPath id='clip0_2001_1903'>
              <rect width='16' height='16' fill='white' />
            </clipPath>
          </defs>
        </svg>
        <DrawerComponent open={open} zIndex={50} percentage='87' toggleDrawer={handleDrawerClose}>
          <FlexCol className='p-10'>
            <h1 className='mb-8 text-black'>Действия</h1>
            <AddActionGop
              toggleDoing={setOpenDrawer}
              isDisabled={true}
              isAutoSave={true}
              firstActionId={firstActionId}
              onActionAdded={() => {
                closeDialog();
              }}
              context={{ ...context, isPrefillMode: true }}
            />
          </FlexCol>
        </DrawerComponent>
      </>
    );
  }

  return (
    <>
      <svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16' fill='none' className='group cursor-pointer' onClick={() => setOpenDrawer(true)}>
        <g clipPath='url(#clip0_2001_28231)'>
          <path
            d='M10.5504 7.42539L8.55039 7.42539V5.45039C8.55039 5.15039 8.30039 4.90039 7.97539 4.90039C7.67539 4.90039 7.42539 5.15039 7.42539 5.47539V7.45039H5.45039C5.15039 7.45039 4.90039 7.70039 4.90039 8.02539C4.90039 8.32539 5.15039 8.57539 5.47539 8.57539H7.45039L7.45039 10.5504C7.45039 10.8504 7.70039 11.1004 8.02539 11.1004C8.32539 11.1004 8.57539 10.8504 8.57539 10.5254V8.55039H10.5504C10.8504 8.55039 11.1004 8.30039 11.1004 7.97539C11.1004 7.67539 10.8504 7.42539 10.5504 7.42539Z'
            fill='#9CA3AF'
            className='group-hover:hidden'
          />
          <path
            d='M8.0002 0.450195C3.8252 0.450195 0.450195 3.8252 0.450195 8.0002C0.450195 12.1752 3.85019 15.5752 8.0252 15.5752C12.2002 15.5752 15.6002 12.1752 15.6002 8.0002C15.5752 3.8252 12.1752 0.450195 8.0002 0.450195ZM8.0002 14.4502C4.4502 14.4502 1.5752 11.5502 1.5752 8.0002C1.5752 4.4502 4.4502 1.5752 8.0002 1.5752C11.5502 1.5752 14.4502 4.4502 14.4502 8.0002C14.4502 11.5502 11.5502 14.4502 8.0002 14.4502Z'
            fill='#9CA3AF'
            className='group-hover:hidden'
          />
          <path
            xmlns='http://www.w3.org/2000/svg'
            fillRule='evenodd'
            clipRule='evenodd'
            d='M0.450195 8.0002C0.450195 3.8252 3.8252 0.450195 8.0002 0.450195C12.1752 0.450195 15.5752 3.8252 15.6002 8.0002C15.6002 12.1752 12.2002 15.5752 8.0252 15.5752C3.85019 15.5752 0.450195 12.1752 0.450195 8.0002ZM8.55023 7.42523H10.5502C10.8502 7.42523 11.1002 7.67523 11.1002 7.97523C11.1002 8.30023 10.8502 8.55023 10.5502 8.55023H8.57523V10.5252C8.57523 10.8502 8.32523 11.1002 8.02523 11.1002C7.70023 11.1002 7.45023 10.8502 7.45023 10.5502V8.57523H5.47523C5.15023 8.57523 4.90023 8.32523 4.90023 8.02523C4.90023 7.70023 5.15023 7.45023 5.45023 7.45023H7.42523V5.47523C7.42523 5.15023 7.67523 4.90023 7.97523 4.90023C8.30023 4.90023 8.55023 5.15023 8.55023 5.45023V7.42523Z'
            fill='#9CA3AF'
            className='hidden group-hover:block'
          />
        </g>
        <defs>
          <clipPath id='clip0_2001_28231'>
            <rect width='16' height='16' fill='white' />
          </clipPath>
        </defs>
      </svg>
      <DrawerComponent open={open} zIndex={50} percentage='87' toggleDrawer={handleDrawerClose}>
        <FlexCol className='p-10'>
          <h1 className='mb-8 text-black'>Действия</h1>
          <AddActionGop
            toggleDoing={setOpenDrawer}
            isDisabled={true}
            firstActionId={firstActionId}
            onActionAdded={() => {
              closeDialog();
            }}
            context={{ ...context, isPrefillMode: true }}
          />
        </FlexCol>
      </DrawerComponent>
    </>
  );
}

export default AddAction;
